import Vue from 'vue';
import { platWindow, isNative, getOS } from '@/utils/env';

let _window;

let windowDimensions;

const initDimensions = () => {
  if (!_window) {
    _window = platWindow;
  }
  const ratio = 1;
  windowDimensions = {
    height: isNative
      ? Vue.Native.Dimensions.screen.height
        ? Vue.Native.Dimensions.screen.height
        : Vue.Native.Dimensions.window.height
      : _window.innerHeight / ratio,
    width: isNative
      ? Vue.Native.Dimensions.screen.width
        ? Vue.Native.Dimensions.screen.width
        : Vue.Native.Dimensions.window.width
      : _window.innerWidth,
  };
};
export const getFullHeight = () => {
  initDimensions();
  return windowDimensions.height;
};

export const getWindowHeight = () => {
  if (!_window) _window = platWindow;
  const ratio = 1;
  return isNative ? Vue.Native.Dimensions.window.height : _window.innerHeight / ratio;
};

export const getFullWidth = () => {
  initDimensions();
  return windowDimensions.width;
};

/**
 * 根据屏幕的缩放，获取对应的px值
 * - 以iphone6的宽度为基准，计算相关缩放后的px值
 * @return { Number } 缩放处理后的px值
 * @example
 *   getPxByScreenScale(100) => 100 // iphone6
 */
export const getPxByScreenScale = (() => {
  const scale = getFullWidth() / 375;

  return px => Math.round(px * scale);
})();

export const getPixelRatio = () => Vue.Native.PixelRatio;

export const calcNum = num => `${num}px`;
// return isNative ? num : `${num}px`;

export const isIOS = () => {
  if (!_window) {
    _window = platWindow;
  }
  if (!isNative) {
    return getOS(_window.navigator.userAgent) === 'ios';
  }
  return Vue.Native && Vue.Native.Platform === 'ios';
};
export const isAndroid = () => {
  if (!_window) {
    _window = platWindow;
  }
  if (!isNative) {
    return getOS(_window.navigator.userAgent) === 'and';
  }
  return Vue.Native && Vue.Native.Platform === 'android';
};
export const isIPhoneX = () => Vue.Native && Vue.Native.Platform === 'ios' && Vue.Native.isIPhoneX;

export const statusBarHeight = () => {
  if (Vue.Native && Vue.Native.Dimensions.window.statusBarHeight) {
    return Vue.Native.Dimensions.window.statusBarHeight;
  }
  return isAndroid() ? 18 : (isIPhoneX() ? 44 : 20);
};

export const getStatusBarHeight = () => {
  if (Vue.Native && Vue.Native.Dimensions.window.statusBarHeight) {
    return Vue.Native.Dimensions.window.statusBarHeight;
  }
  return isAndroid() ? 18 : (isIPhoneX() ? 44 : 20);
};

export const transformSize = (size) => {
  // const ratio = 1
  const screenWidth = isNative ? Vue.Native.Dimensions.screen.width : window.innerWidth;
  // let screenHeight = isNative ? Vue.Native.Dimensions.screen.height : window.innerHeight / ratio
  const scale = screenWidth * 100.0 / 375 / 100;
  return `${size * scale}px`;
};

/**
 * 获取数据的类型
 * @param {Any} data 需要判断类型的数据
 */
const getDataType = data => (Object.prototype.toString.call(data)).match(/\[object (\w+)\]/)[1];

/**
 * @description 将字符串从驼峰转化为下划线，css样式key转换
 * @param {String} name 字符串
 */
export const underline = name => name.replace(/\B([A-Z])/g, '_$1').toLowerCase();

export const getNumberKey = name => ['flex', 'opacity', 'fontWeight'].includes(name);
/**
 * @description hippy 和 web 样式兼容，统一使用create函数，根据平台返回对应的样式，放在style中使用
 * 参考资料 https://juejin.im/post/5c6a151f518825625e4ac830
 * http://cdn.jsnewbee.com/files/%E5%A4%9A%E7%AB%AF%E7%BB%9F%E4%B8%80%E5%BC%80%E5%8F%91%E5%AE%9E%E8%B7%B5.pdf
 * @param {Object} raw 原始样式
 */
export const create = (raw) => {
  if (getDataType(raw) !== 'Object') {
    console.warn('StyleSheet create只支持Object');
    return {};
  }
  const style = {};
  Object.keys(raw).forEach((key) => {
    if (getDataType(raw[key]) === 'Array') {
      raw[key] = Object.assign({}, ...raw[key]);
    } else if (getDataType(raw[key]) !== 'Object') {
      return;
    }
    style[key] = {};
    Object.keys(raw[key]).forEach((field) => {
      style[key][field] = getDataType(raw[key][field]) === 'Number' && !getNumberKey(field) ? calcNum(raw[key][field]) : raw[key][field];
      if (isNative) {
        if (raw[key][field] === '100%') { // 浏览器端使用 100%, native 获取窗口高宽 ， 这里有一定争议，因为 100%是相对于父元素的
          if (/width/i.test(field)) {
            style[key][field] = getFullWidth();
          } else if (/height/i.test(field)) {
            style[key][field] = getWindowHeight();
          }
        } else if (typeof raw[key][field] === 'string' && raw[key][field].indexOf('calc(') === 0) {
          let data = raw[key][field].replace('calc(', '').replace(/\)$/, '');
          data = data.replace(/vw/g, `*${getFullWidth() / 100}`);
          data = data.replace(/vh/g, `*${getFullHeight() / 100}`);
          data = data.replace(/px/g, '');

          try {
            // eslint-disable-next-line no-eval
            data = eval(data);
          } catch (e) {
            console.error(data);
            console.error(e);
          }
          style[key][field] = data;
        }
      }
      if (!isNative) {
        if (field === 'resizeMode') {
          if (raw[key][field] === 'cover' || raw[key][field] === 'contain') {
            style[key]['object-fit'] = raw[key][field];
          } else if (raw[key][field] === 'stretch') {
            style[key]['object-fit'] = 'fill';
          }
        } else if (field === 'paddingVertical' || field === 'marginVertical') { // web 不支持这个属性
          const type = field === 'paddingVertical' ? 'padding' : 'margin';
          if (!style[key][`${type}Top`]) {
            style[key][`${type}Top`] = calcNum(raw[key][field]);
          }
          if (!style[key][`${type}Bottom`]) {
            style[key][`${type}Bottom`] = calcNum(raw[key][field]);
          }
        } else if (field === 'paddingHorizontal' || field === 'marginHorizontal') {
          const type = field === 'paddingHorizontal' ? 'padding' : 'margin';
          if (!style[key][`${type}Right`]) {
            style[key][`${type}Right`] = calcNum(raw[key][field]);
          }
          if (!style[key][`${type}Left`]) {
            style[key][`${type}Left`] = calcNum(raw[key][field]);
          }
        }
      }
    });
  });
  return style;
};

export const variables = {
  // 色值
  $color_main: '#FFDE02',
  $color_highlight: '#FCA100',
  // $color_title: '#000000',
  // $color_article: '#4c4c4c',
  // $color_assist: '#999999',
  // $color_label_text: '#b2b2b2',
  // $color_link_text: '#0179ff',
  // $color_icon: '#cccccc',

  // 按钮
  $button_radius: 6,

  // 头像
};

export const fullStyle = {
  display: 'flex',
  flex: '1',
  flexDirection: 'column',
};

export default {
  create,
  variables,
};
